import { ReactNode, Ref, useId } from "react"

import * as RadioGroupPrimitive from "@radix-ui/react-radio-group"

import { cn } from "@/lib/utils"

import { Input, InputProps } from "./Input"

export function Root({
  className,
  ref,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Root> & {
  label?: string
  ref?: Ref<React.ComponentRef<typeof RadioGroupPrimitive.Root>>
}) {
  return (
    <RadioGroupPrimitive.Root
      className={cn("flex flex-col gap-3", className)}
      ref={ref}
      {...props}
    />
  )
}

export function Item({
  label,
  className,
  ref,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item> & {
  label?: ReactNode
  ref?: Ref<React.ComponentRef<typeof RadioGroupPrimitive.Item>>
}) {
  const id = useId()
  const radioId = props.id || id

  return (
    <span
      className={cn("relative flex items-center text-body1 text-foreground", className)}
    >
      <RadioGroupPrimitive.Item
        id={radioId}
        ref={ref}
        {...props}
        className={cn(
          "focus-outline",
          "group peer flex h-3 w-3 shrink-0 items-center justify-center rounded-full",
          "border border-muted-foreground",
          "disabled:cursor-not-allowed disabled:border-muted-foreground/60",
          "aria-checked:border-primary-foreground aria-checked:bg-primary",
          "aria-checked:disabled:border-muted-foreground/60",
        )}
      >
        <RadioGroupPrimitive.Indicator className="inline-block h-1.5 w-1.5 rounded-full bg-primary-foreground group-disabled:border-muted-foreground/60 group-disabled:bg-muted-foreground/40" />
      </RadioGroupPrimitive.Item>
      {label ? (
        <label
          className="cursor-pointer pl-2 peer-disabled:cursor-not-allowed peer-disabled:text-muted-foreground/60"
          htmlFor={radioId}
        >
          {label}
        </label>
      ) : null}
    </span>
  )
}

export function ItemOther({
  label = "Other:",
  value,
  onChange,
  placeholder,
  ref,
  ...props
}: Omit<React.ComponentProps<typeof RadioGroupPrimitive.Item>, "value" | "onChange"> & {
  label?: string
  value: InputProps["value"]
  onChange: InputProps["onChange"]
  placeholder?: InputProps["placeholder"]
  ref?: Ref<React.ComponentRef<typeof RadioGroupPrimitive.Item>>
}) {
  return (
    <div className="flex flex-col gap-3">
      <Item label={label} {...props} value="other" ref={ref} />
      <Input value={value} onChange={onChange} placeholder={placeholder} />
    </div>
  )
}

export function ItemLabelOnly({
  label,
  screenReaderText,
  className,
  ref,
  ...props
}: React.ComponentProps<typeof RadioGroupPrimitive.Item> & {
  label: string | ReactNode
  screenReaderText?: string
  ref?: Ref<React.ComponentRef<typeof RadioGroupPrimitive.Item>>
}) {
  const radioId = useId()
  // ItemLabelOnly hides radio control

  return (
    <span
      className={
        "relative flex items-center overflow-hidden text-body1 text-foreground"
      }
    >
      <RadioGroupPrimitive.Item id={radioId} ref={ref} {...props}>
        <label htmlFor={radioId}>
          <div
            className={cn(
              "cursor-pointer peer-disabled:cursor-default peer-disabled:text-muted-foreground",
              className,
            )}
          >
            {label}
          </div>
          {/* Label text should only be for screen readers */}
          <span className="sr-only">{screenReaderText}</span>
        </label>
      </RadioGroupPrimitive.Item>
    </span>
  )
}
